import React from 'react';
import _capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { Play } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { DotSeparator, Stack, Typography } from '@lyearn/ui';
import { Link } from '@/components/Link';
import { getOverdue } from '@/modules/content/components/ContentCard/utils/getOverdue';
import { ContentOverdueDueEnum } from '@/modules/content/components/ContentCard/utils/types';
import { isEventRegistered } from '@/modules/events/views/EventDetails/components/RecurringInstanceCarousal/Card';
import { ContentEntity, EventUnion, ResourceTypeEnum, WidgetTypeEnum } from '@/types/schema';
import getHumanDuration from '@/utils/time/getHumanDurationRounded';

import {
  getEntityDuration,
  getEntityId,
  getEntityImage,
  getEntityName,
  getEntityStartTime,
  getEntityUrl,
  getIsEventEntity,
} from '../../../../utils/homepageWidgetsDataAdapterUtil';
import DueOn from '../../../HomepageList/components/DueOn';
import Progress from '../../../HomepageList/components/Progress';
import RegisteredPill from '../../../HomepageList/components/Registered';
import { WidgetEntityUnion } from '../../../types';

type Props = {
  entity: WidgetEntityUnion;
  index: number;
  rowSpanClass: string;
  colSpanClass: string;
  widgetType: WidgetTypeEnum;
  /**
   * in case of microlearning card grid,
   * widget id is used when linking to the lesson directly
   */
  widgetId?: string;
};

export const CONTENT_TYPE_WIDGETS = [
  WidgetTypeEnum.Contents,
  WidgetTypeEnum.CompletedLearnings,
  WidgetTypeEnum.ContentPerformanceWidget,
  WidgetTypeEnum.RefresherContents,
  WidgetTypeEnum.ContinueLearning,
  WidgetTypeEnum.SelfEnrolledContents,
  WidgetTypeEnum.AssignedContents,
  WidgetTypeEnum.Calendar,
];

const GalleryGridCard = (props: Props) => {
  const { entity, index, rowSpanClass, colSpanClass, widgetType, widgetId } = props;
  const { t } = useTranslation();
  const { isMobile } = useScreen();

  if (!entity || isEmpty(entity)) {
    return null;
  }

  const entityId = getEntityId(entity, widgetType);
  const view = getEntityUrl(entity, widgetType, widgetId);
  const entityName = getEntityName(entity, widgetType) || '';
  const entityImage = getEntityImage(entity, widgetType) || '';
  const isEventEntity = getIsEventEntity(widgetType);
  const entityDuration = getEntityDuration(entity, widgetType); // for content items
  const entityStartTime = getEntityStartTime(entity, widgetType); // for event items
  const overdue = CONTENT_TYPE_WIDGETS.includes(widgetType)
    ? getOverdue(t, entity as ContentEntity)
    : null;
  const showRegisteredPill = isEventRegistered(entity as EventUnion);
  // @ts-ignore
  const progress = entity?.performance?.progress;

  return (
    <Link key={entityId} className={cn(rowSpanClass, colSpanClass)} to={view}>
      <Stack
        key={index}
        alignItems="start"
        className={cn(
          'relative bg-bg-cyan overflow-hidden cursor-pointer',
          index === 0 ? 'row-start-1 row-end-3' : '',
          'transform transition-all duration-500 ease-out hover:-translate-y-4',
          isMobile ? 'p-8 rounded-16' : 'p-18 rounded-16',
        )}
        direction="col"
        id={`gallery-item-${index}`}
        justifyContent="end"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.7)), url(${entityImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '100%',
        }}>
        <Stack
          className={cn(
            isMobile ? 'top-8 start-8' : 'top-16 start-16',
            'absolute z-10 flex flex-wrap items-start',
          )}
          gapX={8}
          gapY={4}>
          <Stack className="flex flex-wrap" gapX={8} gapY={4}>
            {progress ? <Progress progress={progress} /> : null}
            {overdue && overdue?.status !== ContentOverdueDueEnum.Completed ? (
              <DueOn overdue={overdue} showIcon={true} />
            ) : null}
            {showRegisteredPill ? <RegisteredPill /> : null}
          </Stack>
        </Stack>

        {/* Center PlayCircle */}
        <div className="absolute inset-0 flex items-center justify-center">
          {entity.type === ResourceTypeEnum.MicroLearning ? (
            <Play className="h-64 w-64" fillColorToken="icon-white" />
          ) : null}
        </div>

        <Stack className="max-w-full" direction="col" gapY={4}>
          <DotSeparator
            dotProps={{
              background: 'bg-text-white',
            }}
            gapX={8}
            style={{
              opacity: 0.7,
            }}>
            <Typography
              className="text-text-white"
              numberOfLines={1}
              variant={isMobile ? 'label-03' : 'label-caps'}>
              {_capitalize(entity.type)}
            </Typography>
            {entityDuration || entityStartTime ? (
              <Typography
                className="text-text-white"
                numberOfLines={1}
                variant={isMobile ? 'label-03' : 'label-caps'}>
                {isEventEntity ? entityStartTime : getHumanDuration(entityDuration)}
              </Typography>
            ) : null}
          </DotSeparator>
          <Typography
            className="text-text-white"
            numberOfLines={2}
            variant={isMobile ? 'heading-xxs' : 'heading-s'}>
            {entityName}
          </Typography>
        </Stack>
      </Stack>
    </Link>
  );
};

export default GalleryGridCard;
